import * as React from "react";

import { ReactElement, useState, useEffect } from "react";
import ClipLoader from "react-spinners/ClipLoader";


import './scanresult.scss'
import { DocQrVerdictCode, IDocQr, ListFase, PhaseConflict, ScanResult } from "../../domain/docqr.domain";
import { formatRevisionDateForDisplay } from '../../utils/revisionDate';


export interface IScanResultProps {
  docQr: IDocQr;
  scannedDocumentNumber?: string;
  scannedRevision?: string;
  onScannedRevisionDateChanged?: (revisionDate: string | Date | undefined) => void;
}

export function ScanResultComponent({ docQr, scannedDocumentNumber, scannedRevision, onScannedRevisionDateChanged }: IScanResultProps): ReactElement {

  const [loading, setLoading] = useState<boolean>(false);
  const [scanResult, setScanResult] = useState<ScanResult>();
  const [showExpiredAndDeletedDocuments, setShowExpiredAndDeletedDocuments] = useState<boolean>(false);

  const onScanResultLoadingChanged = (loading: boolean) : void => {
    setLoading(loading);
  };

  const onScanResultChanged = (scanResult: ScanResult) : void => {
    setShowExpiredAndDeletedDocuments(false);
    setScanResult(scanResult);
  };

  const phaseConflicts = scanResult?.phaseConflicts ?? [];
  const verdictAlert = scanResult ? createVerdictAlert(scanResult.verdictCode, phaseConflicts) : undefined;
  const hasExpiredOrDeletedDocuments = scanResult?.documents.some(document =>
    document.status === "Vervallen" || document.status === "Verwijderd"
  ) === true;
  const visibleDocuments = scanResult?.documents.filter(document =>
    showExpiredAndDeletedDocuments ||
    (document.status !== "Vervallen" && document.status !== "Verwijderd")
  ) ?? [];

  useEffect(() => {
    const loadingSubscription = docQr.scanResultLoadingObservable.subscribe(onScanResultLoadingChanged);
    const resultsSubscription = docQr.scanResultObservable.subscribe(onScanResultChanged);

    return () => {
      loadingSubscription.unsubscribe();
      resultsSubscription.unsubscribe();
    }
  }, [docQr.scanResultLoadingObservable, docQr.scanResultObservable]);

  useEffect(() => {
    if (!onScannedRevisionDateChanged) {
      return;
    }

    onScannedRevisionDateChanged(findScannedDocumentRevisionDate(scanResult, scannedDocumentNumber, scannedRevision));
  }, [scanResult, scannedDocumentNumber, scannedRevision, onScannedRevisionDateChanged]);

    return (
      <>
        { loading && 
          <div className="d-flex flex-row">
            <div className="p-2"><ClipLoader loading={loading} color="#497cab" speedMultiplier={0.8} /></div>
            <div className="p-2">de scanresultaten worden geladen ...</div>
          </div>
        }

        { !loading && scanResult && scanResult.isSuccess && 
        <>
            { verdictAlert &&
              <div className={verdictAlert.className} role="alert">{verdictAlert.message}</div>
            }

            { scanResult.documents.length > 0 &&
              <>
                { hasExpiredOrDeletedDocuments &&
                  <label className="scanresult-document-filter" htmlFor="show-expired-and-deleted-documents">
                    <input
                      id="show-expired-and-deleted-documents"
                      className="scanresult-document-filter__checkbox"
                      type="checkbox"
                      checked={showExpiredAndDeletedDocuments}
                      onChange={event => setShowExpiredAndDeletedDocuments(event.target.checked)}
                    />
                    <span>Toon inclusief vervallen en verwijderde documenten</span>
                  </label>
                }
                { visibleDocuments.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Document</th>
                          <th>Revisie</th>
                          <th>Revisiedatum</th>
                          <th>Bibliotheek</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          visibleDocuments.map((document, i) => {

                            return (
                              <tr key={`${document.documentNumber}-${document.revision}-${i}`} className={document.status === "Vervallen" || document.status === "Verwijderd" ? "table-danger" : ""}>
                                <td>
                                { document.status !== "Verwijderd" && <a target="_blank" rel="noopener noreferrer" href={document.documentUrl}>{document.documentName}</a>  }
                                { document.status === "Verwijderd" && document.documentName }
                                </td>
                                <td>{document.revision}</td>
                                <td>{formatRevisionDateForDisplay(document.revisionDate)}</td>
                                <td>{document.listName}</td>
                                <td>{document.status}</td>
                              </tr>)
                          })
                        }

                        </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="alert alert-info" role="status">
                    Alle gevonden documenten zijn vervallen of verwijderd. Vink de optie aan om ze te tonen.
                  </div>
                )}
              </>
            }          
        </> 
       }

       { !loading && scanResult && !scanResult.isSuccess &&
          <div className="alert alert-danger" role="alert">Er is een fout opgetreden bij het laden van het scanresultaat: {scanResult.errorMessage}</div>
       }

      </>
    );
}

interface VerdictAlert {
  message: string;
  className: string;
}

const verdictAlerts: Record<Exclude<DocQrVerdictCode, 'PhaseConflict'>, VerdictAlert> = {
  CannotDetermine: {
    message: 'De hoogste revisie van het document kan niet worden bepaald. De revisienummers hebben een afwijkend of ongeldig formaat. Bepaal zelf de hoogste revisie in de onderstaande tabel.',
    className: 'alert alert-info'
  },
  LatestReleasedForExecution: {
    message: 'Dit is de hoogste revisie die is vrijgegeven voor uitvoering.',
    className: 'alert alert-success'
  },
  LatestReleasedForExecutionWithPublishedHigher: {
    message: 'Dit is de hoogste revisie die is vrijgegeven voor uitvoering. Let op! Er is een hogere revisie aanwezig in vrijgegeven.',
    className: 'alert alert-success'
  },
  LatestReleasedForExecutionWithSharedHigher: {
    message: 'Dit is de hoogste revisie die is vrijgegeven voor uitvoering. Let op! Er is een hogere revisie aanwezig in gedeeld.',
    className: 'alert alert-success'
  },
  LatestNotReleasedForExecution: {
    message: 'Dit is de hoogste revisie van het document, maar het document is niet vrijgegeven voor uitvoering.',
    className: 'alert alert-danger'
  },
  LatestPublished: {
    message: 'Dit is de hoogste revisie van het document.',
    className: 'alert alert-success'
  },
  LatestNotPublished: {
    message: 'Dit is de hoogste revisie van het document. Let op! Het document is nog niet vrijgegeven.',
    className: 'alert alert-danger'
  },
  LatestPublishedWithHigherShared: {
    message: 'Dit is de hoogste vrijgegeven revisie van het document. Let op! Er is een hogere revisie aanwezig in gedeeld.',
    className: 'alert alert-success'
  },
  Superseded: {
    message: 'Dit is niet de hoogste revisie van het document.',
    className: 'alert alert-danger'
  },
  ExpiredAndDeleted: {
    message: 'Er is geen bruikbare actuele registratie van dit document; registraties zijn vervallen en verwijderd.',
    className: 'alert alert-danger'
  },
  Expired: {
    message: 'Deze revisie van het document is vervallen.',
    className: 'alert alert-danger'
  },
  RevisionDeleted: {
    message: 'Deze revisie van het document is verwijderd.',
    className: 'alert alert-danger'
  },
  DocumentDeleted: {
    message: 'Dit document is verwijderd.',
    className: 'alert alert-danger'
  },
  NoDocuments: {
    message: 'Er zijn geen documenten gevonden.',
    className: 'alert alert-danger'
  }
};

const unknownVerdictAlert: VerdictAlert = {
  message: 'De uitkomst van de controle kan niet worden weergegeven. Bepaal zelf de status van het document in de onderstaande tabel.',
  className: 'alert alert-secondary'
};

function createVerdictAlert(verdictCode: string, phaseConflicts: PhaseConflict[]): VerdictAlert {
  if (verdictCode === 'PhaseConflict') {
    return { message: createPhaseConflictMessage(phaseConflicts), className: 'alert alert-danger' };
  }

  return Object.prototype.hasOwnProperty.call(verdictAlerts, verdictCode)
    ? verdictAlerts[verdictCode as keyof typeof verdictAlerts]
    : unknownVerdictAlert;
}

const phaseLabels: Record<ListFase, string> = {
  Gedeeld: 'Gedeeld',
  Vrijgegeven: 'Vrijgegeven',
  VrijgegevenVoorUitvoering: 'Vrijgegeven voor uitvoering'
};

function createPhaseConflictMessage(phaseConflicts: PhaseConflict[]): string {
  const phaseNames = phaseConflicts.map(conflict => phaseLabels[conflict.phase] ?? conflict.phase);
  const phaseDescription = phaseNames.length === 0
    ? 'een of meer fasen'
    : `${phaseNames.length === 1 ? 'de fase' : 'de fasen'} ${formatDutchList(phaseNames)}`;

  return `Controle niet mogelijk: er staan meerdere documenten met dit documentnummer in ${phaseDescription}. Daardoor kan geen betrouwbaar oordeel worden gegeven.`;
}

function formatDutchList(values: string[]): string {
  if (values.length <= 1) {
    return values[0] ?? '';
  }

  return `${values.slice(0, -1).join(', ')} en ${values[values.length - 1]}`;
}

function findScannedDocumentRevisionDate(
  scanResult: ScanResult | undefined,
  scannedDocumentNumber?: string,
  scannedRevision?: string
): string | Date | undefined {
  if (!scanResult || !scanResult.isSuccess || scanResult.documents.length <= 0) {
    return undefined;
  }

  const normalizedDocumentNumber = scannedDocumentNumber?.toUpperCase();
  const normalizedRevision = scannedRevision?.toUpperCase();

  const matchedDocument = scanResult.documents.find(document => {
    const documentNumberMatches = !normalizedDocumentNumber || document.documentNumber.toUpperCase() === normalizedDocumentNumber;
    const revisionMatches = !normalizedRevision || document.revision.toUpperCase() === normalizedRevision;

    return documentNumberMatches && revisionMatches;
  });

  return matchedDocument?.revisionDate;
}
